import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { CandidateCard } from "@/components/CandidateCard";
import { OntarioMapView } from "@/components/OntarioMapView";
import { api } from "@/lib/api";
import { MapPin, Users, ExternalLink, Landmark, ShieldCheck, AlertCircle, Search } from "lucide-react";

export default function FindMyWard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [address, setAddress] = useState(initialQ);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [wyvCandidates, setWyvCandidates] = useState([]);

  const lookup = async (q) => {
    if (!q?.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setWyvCandidates([]);
    try {
      const r = await api.get("/public/geo/locate", { params: { q: q.trim() } });
      setResult(r.data);
      if (r.data.found && r.data.wyv?.municipality) {
        try {
          const cr = await api.get("/public/candidates");
          const wardLabel = r.data.ward?.ward_label;
          const muniId = r.data.wyv.municipality.id;
          setWyvCandidates(
            cr.data.filter(
              (c) =>
                c.municipality?.id === muniId &&
                (!wardLabel || !c.office?.ward_label || c.office.ward_label === wardLabel)
            )
          );
        } catch {}
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Lookup failed. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialQ) lookup(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setSearchParams(address.trim() ? { q: address.trim() } : {});
    lookup(address);
  };

  const muni = result?.municipality;
  const ward = result?.ward;

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Find My Ward</Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="find-my-ward-title">
          Find your ward from your address.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Type your address to locate your municipality — and your ward where boundary data is available —
          then see who is running and continue to Witness Your Vote.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <label htmlFor="ward-address" className="sr-only">Your address</label>
        <Input
          id="ward-address"
          placeholder="e.g. 26 Francis St, Lindsay"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          data-testid="ward-address-input"
          className="flex-1 h-11"
        />
        <Button type="submit" className="h-11 bg-accent hover:bg-accent/90" disabled={loading} data-testid="ward-address-submit">
          <Search className="h-4 w-4 mr-1.5" /> {loading ? "Locating…" : "Find My Ward"}
        </Button>
      </form>

      {error && (
        <Card className="mt-6 max-w-2xl border-red-200 bg-red-50" data-testid="ward-error">
          <CardContent className="p-4 text-sm text-red-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-[420px] rounded-xl" />
          <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-40" /></div>
        </div>
      )}

      {result && !result.found && (
        <Card className="mt-6 max-w-2xl" data-testid="ward-not-found">
          <CardContent className="p-5 text-sm text-muted-foreground">{result.message}</CardContent>
        </Card>
      )}

      {result?.found && (
        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <OntarioMapView
              focusMuniSlug={muni?.slug || null}
              focusWardLabel={ward?.ward_label || null}
              point={result.point}
              heightClass="h-[380px] sm:h-[520px]"
            />
            <p className="text-[11px] text-muted-foreground mt-2">
              Boundaries: {result.boundary_source?.name}. Map © OpenStreetMap contributors.
            </p>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <Card data-testid="ward-result-card">
              <CardContent className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-accent mb-2">Your result</p>
                {result.address && (
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="ward-detected-address">
                    <MapPin className="h-3.5 w-3.5 inline mr-1 text-accent" />{result.address}
                  </p>
                )}
                <div className="mt-3 space-y-2">
                  <p className="flex items-center gap-2 text-sm" data-testid="ward-result-muni">
                    <Landmark className="h-4 w-4 text-primary" />
                    <span className="font-heading font-semibold text-foreground">{muni ? muni.name : "Municipality not identified"}</span>
                    {muni?.type && <Badge variant="outline" className="text-[10px]">{muni.type}</Badge>}
                  </p>
                  {muni?.upper_tier && (
                    <p className="text-xs text-muted-foreground">Part of: {muni.upper_tier}</p>
                  )}
                  <p className="flex items-center gap-2 text-sm" data-testid="ward-result-ward">
                    <MapPin className="h-4 w-4 text-primary" />
                    {ward ? (
                      <span className="font-heading font-semibold text-foreground">{ward.ward_label}</span>
                    ) : result.ward_data_available ? (
                      <span className="text-muted-foreground">Address is outside available ward boundaries</span>
                    ) : (
                      <span className="text-muted-foreground">Ward boundary data not yet available for this municipality</span>
                    )}
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-4 border-t border-slate-100 pt-3" data-testid="ward-disclaimer">
                  {result.disclaimer}
                </p>
              </CardContent>
            </Card>

            {wyvCandidates.length > 0 && (
              <div data-testid="ward-wyv-candidates">
                <h2 className="font-heading font-semibold text-base text-foreground mb-3 inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent" /> Participating Witness Your Vote candidates
                </h2>
                <div className="space-y-3">
                  {wyvCandidates.map((c) => <CandidateCard key={c.id} candidate={c} declareMode />)}
                </div>
              </div>
            )}

            {result.wyv?.municipality && wyvCandidates.length === 0 && (
              <Card>
                <CardContent className="p-5 text-sm text-muted-foreground">
                  No participating candidate in {ward?.ward_label || "your area"} yet.{" "}
                  <Link to="/invite-a-candidate" className="text-primary font-medium hover:underline">Invite one</Link>.
                </CardContent>
              </Card>
            )}

            {result.imported_candidates?.length > 0 && (
              <Card data-testid="ward-imported-candidates">
                <CardContent className="p-5">
                  <h2 className="font-heading font-semibold text-sm text-foreground mb-2 inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" /> Candidates from official sources
                  </h2>
                  <ul className="space-y-2">
                    {result.imported_candidates.map((c) => (
                      <li key={c.id} className="text-sm text-foreground flex items-start justify-between gap-2">
                        <span>{c.full_name}{c.office ? ` — ${c.office}` : ""}{c.ward ? ` (${c.ward})` : ""}</span>
                        <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Imported from official municipal sources by the daily 6:00 PM Eastern scan. Verify with the official source.
                  </p>
                </CardContent>
              </Card>
            )}

            {!result.wyv?.municipality && muni && (
              <Card>
                <CardContent className="p-5 text-sm text-muted-foreground">
                  {muni.name} is not yet an active Witness Your Vote municipality.{" "}
                  <Link to="/contact" className="text-primary font-medium hover:underline">Request it</Link> or{" "}
                  <Link to="/invite-a-candidate" className="text-primary font-medium hover:underline">invite a candidate</Link>.
                </CardContent>
              </Card>
            )}

            <div className="flex flex-wrap gap-3">
              <Button asChild variant="outline" size="sm"><Link to="/elections/map">Open the Ontario Election Map</Link></Button>
              <Button asChild variant="outline" size="sm"><Link to="/elections">Election Centre</Link></Button>
            </div>
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="mt-10">
          <h2 className="font-heading font-semibold text-lg text-foreground mb-3">Prefer to browse?</h2>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="ward-browse-map"><Link to="/elections/map">Ontario Election Map</Link></Button>
            <Button asChild variant="outline"><Link to="/elections">Election Centre</Link></Button>
            <Button asChild variant="outline"><Link to="/candidates">Participating Candidates</Link></Button>
          </div>
          <Card className="mt-6 bg-secondary max-w-3xl">
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Ward boundaries and voter eligibility are determined by your municipality. For an exact address-based
                ward confirmation and official election information, consult your municipal Clerk's office or the
                municipality's website <ExternalLink className="h-3 w-3 inline" />.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
